define('presentation/welcome/viewModels/onboardingIdentityViewModel',[
    'i18next',
    'businessServices/converters/textConverter',
    'common/collections/enumerationKeyValueConverter',
    'common/converters/phoneNumberFormatter',
    'common/promises/promiseFactory',
    'constants/companySizeEnumerations',
    'constants/countryEnumerations',
    'constants/liveReceptionistOnboardingStatusConstants',
    'constants/liveReceptionistScriptAgentToneConstants',
    'constants/marketingIndustryEnumerations',
    'constants/signupNumberIntentConstants',
    'presentation/welcome/common/onboardingNavigationManager',
    'presentation/welcome/common/onboardingSectionValidationDisplay',
    'presentation/welcome/facades/onboardingIdentityFacade',
    'presentation/welcome/validators/onboardingIdentityValidator'
], function() {
    return function() {
        let self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

        const TextConverterConstructor = require('businessServices/converters/textConverter');
        const _textConverter = new TextConverterConstructor();

        const SectionValidationDisplayConstructor = require('presentation/welcome/common/onboardingSectionValidationDisplay');
        const _sectionValidationDisplay = new SectionValidationDisplayConstructor();

        const _i18n = require('i18next');
        const _agentTones = require('constants/liveReceptionistScriptAgentToneConstants');
        const _companySizes = require('constants/companySizeEnumerations');
        const _countryEnumerations = require('constants/countryEnumerations');
        const _industries = require('constants/marketingIndustryEnumerations');
        const _keyValueConverter = require('common/collections/enumerationKeyValueConverter');
        const _liveReceptionistOnboardingStatus = require('constants/liveReceptionistOnboardingStatusConstants');
        const _signupNumberIntentConstants = require('constants/signupNumberIntentConstants');

        let _facade = null;
        let _disposables = [];
        let _onboardingNavigationManager = null;
        let _validator = null;

        const MAX_ITEMS_CAN_ADD = 10;

        self.companySizes = [];
        self.industries = [];

        self.isScrollToOffsetActive = true;
        self.isCompositionComplete = ko.observable(false);
        self.showValidation = ko.observable(false);
        self.isScrollActive = ko.observable(false);

        self.headingText = _i18n.t('onboardingIdentity:headingText');
        self.subtitleText = _i18n.t('onboardingIdentity:subtitleText');
        self.isValid = ko.observable(false);

        self.accountBusinessName = ko.observable('').extend({ rateLimit: 500 });
        self.businessName = ko.observable('').extend({ rateLimit: 500 });
        self.businessDescription = ko.observable('').extend({ rateLimit: 500 });
        self.agentTone = ko.observable();
        self.industry = ko.observable();
        self.numberOfEmployees = ko.observable();

        self.numberIntentSelection = ko.observable(_signupNumberIntentConstants.forward);
        self.mayForwardFromNumbers = ko.observableArray([]);

        // Ops removed the "what number will your callers be dialing?" question from onboarding.
        // The wiring is intentionally left intact - flip this to true to bring the section back.
        const IS_FORWARD_FROM_NUMBERS_SECTION_VISIBLE = false;

        self.isForwardFromNumbersSectionVisible = ko.observable(IS_FORWARD_FROM_NUMBERS_SECTION_VISIBLE);
        self.shouldDisplayForwardFromNumbers = ko.pureComputed(() => {
            return self.isForwardFromNumbersSectionVisible() &&
                self.numberIntentSelection() === _signupNumberIntentConstants.forward;
        });

        _sectionValidationDisplay.init(self.showValidation);
        self.canDisplayForwardFromNumbersValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayForwardFromNumbers);

        self.forwardFromNumbersTabs = [
            { id: _signupNumberIntentConstants.forward, label: _i18n.t('onboardingIdentity:forwardMyOwnNumber') },
            { id: _signupNumberIntentConstants.new, label: _i18n.t('onboardingIdentity:usePATLiveNumber') }
        ];

        self.forwardFromNumbersTooltip = ko.pureComputed(() => _i18n.t("onboardingIdentity:forwardFromNumbersTooltip"));

        self.hasAtLeastOneForwardFromNumber = ko.pureComputed(() => {
            return self.mayForwardFromNumbers()
                .some((forwardFromNumber) => forwardFromNumber.number().trim() !== '');
        });

        self.isNavigateToNextPageActive = ko.pureComputed(() => {
            if (self.isValid() !== true) {
                return false;
            }
            if (self.shouldDisplayForwardFromNumbers() === false) {
                return true;
            }
            const areNumbersValid = self.mayForwardFromNumbers()
                .every((forwardFromNumber) => forwardFromNumber.number.isValid() !== false);
            return self.hasAtLeastOneForwardFromNumber() && areNumbersValid;
        });

        self.canAddAnotherNumber = ko.pureComputed(() => self.mayForwardFromNumbers().length < MAX_ITEMS_CAN_ADD);
        self.addNumberBtnText = ko.pureComputed(() => {
            if (self.canAddAnotherNumber()) {
                return _i18n.t('onboardingIdentity:addPhoneNumber');
            } else {
                return _i18n.t('onboardingIdentity:disabledAddPhoneNumber');
            }
        });

        self.formTitle = ko.pureComputed(() => {
            return _i18n.t('onboardingIdentity:formTitle', { name: self.businessNamePlaceholder() });
        });
        self.businessDescriptionPlaceholder = _i18n.t('onboardingIdentity:businessDescriptionPlaceholder');
        self.businessDescriptionTooltip = _i18n.t('onboardingIdentity:businessDescriptionTooltip');
        self.agentToneTooltip = _i18n.t('onboardingIdentity:agentToneTooltip');
        self.industryTooltip = _i18n.t('onboardingIdentity:industryTooltip');
        self.selectPlaceHolderMessage = _i18n.t("onboardingIdentity:select");

        self.businessNamePlaceholder = ko.pureComputed(() => {
            if (self.businessName()) {
                return self.businessName();
            }
            return _i18n.t('onboardingIdentity:businessNamePlaceholder');
        });

        self.agentToneOptions = [
            {
                id: _agentTones.Professional,
                title: _i18n.t('onboardingIdentity:professionalTone.title'),
                description: ko.pureComputed(() => _i18n.t('onboardingIdentity:professionalTone.description', { name: self.businessNamePlaceholder() })),
                isSelected: ko.pureComputed(() => self.agentTone() === _agentTones.Professional),
                onClick: () => self.agentTone(_agentTones.Professional)
            },
            {
                id: _agentTones.Casual,
                title: _i18n.t('onboardingIdentity:casualTone.title'),
                description: ko.pureComputed(() => _i18n.t('onboardingIdentity:casualTone.description', { name: self.businessNamePlaceholder() })),
                isSelected: ko.pureComputed(() => self.agentTone() === _agentTones.Casual),
                onClick: () => self.agentTone(_agentTones.Casual)
            },
        ];

        self.onRemoveForwardFromNumberClick = (numberToDelete) => {
            if (self.mayForwardFromNumbers().length <= 1) {
                numberToDelete.number('');
                return;
            }
            _disposeNumberSubscriptions(numberToDelete);
            self.mayForwardFromNumbers.remove(numberToDelete);
            _reIndexForwardFromNumbers();
        };

        self.onAddForwardFromNumberClick = () => {
            if (self.canAddAnotherNumber() === false) {
                return;
            }
            _addDefaultForwardFromNumber();
        };

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);
                        _sectionValidationDisplay.captureDisplayedSections();

                        if (!isValid) {
                            deferredObject.resolve(false);
                        } else {
                            deferredObject.resolve(true);
                        }
                    });
            });
        };

        const _handleMayForwardFromNumbersChanged = () => {
            const mayForwardFromNumbers = self.mayForwardFromNumbers()
                .filter((forwardFromNumber) => {
                    const isNumberEmpty = forwardFromNumber.number().trim() === "";
                    const isNumberValid = forwardFromNumber.number.isValid();
                    return !isNumberEmpty && isNumberValid;
                })
                .map((forwardFromNumber) => {
                    return _phoneNumberFormatter.toEOneSixFour(forwardFromNumber.number(), forwardFromNumber.countryAbbreviation());
                });
            _facade.saveMayForwardFromNumbers(mayForwardFromNumbers);
        };

        const _createForwardFromNumber = (index = null) => {
            const forwardFromNumber = {
                index: index,
                countryAbbreviation: ko.observable(_countryEnumerations.usa),
                number: ko.observable('').extend({ rateLimit: 500 })
            };
            forwardFromNumber.isEmpty = ko.pureComputed(() => forwardFromNumber.number().trim() === '');
            forwardFromNumber.disposables = [];
            return forwardFromNumber;
        };

        const _addDefaultForwardFromNumber = () => {
            const forwardFromNumber = _createForwardFromNumber();
            self.mayForwardFromNumbers.push(forwardFromNumber);
            _subscribeToNumberObservables(forwardFromNumber);
            _reIndexForwardFromNumbers();
        };

        const _reIndexForwardFromNumbers = () => {
            self.mayForwardFromNumbers().forEach((number, index) => number.index = index);
        };

        const _subscribeToNumberObservables = (number) => {
            number.disposables.push(number.countryAbbreviation.subscribe(_handleMayForwardFromNumbersChanged));
            number.disposables.push(number.number.subscribe(_handleMayForwardFromNumbersChanged));
        };

        const _disposeNumberSubscriptions = (number) => {
            number.disposables.forEach(subscription => subscription.dispose());
            number.disposables = [];
        };

        const _clearForwardFromNumbers = () => {
            self.mayForwardFromNumbers().forEach(_disposeNumberSubscriptions);
            self.mayForwardFromNumbers([]);
        };

        const _handleAccountBusinessNameChanged = () => {
            _facade.saveAccountBusinessName(self.accountBusinessName());
        };

        const _handleBusinessNameChanged = () => {
            if (self.businessName()) {
                _facade.saveBusinessName(self.businessName());
            }
        };

        const _handleBusinessDescriptionChanged = () => {
            if (self.businessDescription()) {
                _facade.saveBusinessDescription(self.businessDescription());
            }
        };

        const _handleAgentToneChanged = () => {
            if (self.agentTone()) {
                _facade.saveAgentTone(self.agentTone());
            }
        };

        const _handleIndustryChanged = () => {
            if (self.industry()) {
                _facade.saveIndustry(self.industry());
            }
        };

        const _handleNumberOfEmployeesChanged = () => {
            if (self.numberOfEmployees()) {
                _facade.saveNumberOfEmployees(self.numberOfEmployees());
            }
        };

        const _handleNumberIntentSelectionChanged = () => {
            if (self.numberIntentSelection()) {
                _facade.saveNumberIntentSelection(self.numberIntentSelection());
            }

            if (self.numberIntentSelection() === _signupNumberIntentConstants.new) {
                _clearForwardFromNumbers();
            } else if (self.mayForwardFromNumbers().length === 0) {
                _addDefaultForwardFromNumber();
            }

            _revalidateForwardFromNumbers();
        };

        const _revalidateForwardFromNumbers = () => {
            self.mayForwardFromNumbers().forEach((forwardFromNumber) => forwardFromNumber.number.validate());
        };

        const _populateIndustryDropdown = () => {
            self.industries = _keyValueConverter.convertToKeyValues(_industries).map((option) => {
                const i18nKey = _textConverter.camelCase(option.key);
                return { key: option.value, title: _i18n.t(`onboardingIdentity:industries.${i18nKey}`), value: option.value };
            }).sort((a, b) => {
                if (a.title < b.title) {
                    return -1;
                } else if (a.title > b.title) {
                    return 1;
                }
                return 0;
            });
        };

        const _populateNumberOfEmployeesDropdown = () => {
            self.companySizes = _keyValueConverter.convertToKeyValues(_companySizes);
        };

        const _populateForwardFromNumbers = (onboardingSession) => {
            const { mayForwardFromNumbers } = onboardingSession;

            if (mayForwardFromNumbers.length === 0) {
                if (self.numberIntentSelection() === _signupNumberIntentConstants.forward) {
                    _addDefaultForwardFromNumber();
                }
            } else {
                mayForwardFromNumbers.map((forwardFromNumber, index) => {
                    const number = _createForwardFromNumber(index);
                    number.number(_phoneNumberFormatter.toNumbers(forwardFromNumber));
                    self.mayForwardFromNumbers.push(number);
                    _subscribeToNumberObservables(number);
                });
            }
        };

        const _persistDefaultNumberIntentSelection = (onboardingSession) => {
            if (!onboardingSession.numberIntentSelection) {
                _facade.saveNumberIntentSelection(self.numberIntentSelection());
            }
        };

        const _persistWizardStarted = (onboardingSession) => {
            if (onboardingSession.onboardingStatus === _liveReceptionistOnboardingStatus.initiated) {
                _facade.saveOnboardingStatus(_liveReceptionistOnboardingStatus.inProgress);
            }
        };

        const _populateIdentityForm = (onboardingSession) => {
            const { accountBusinessName, businessName, businessDescription, agentTone, industry, numberOfEmployees, numberIntentSelection  } = onboardingSession;
            if (accountBusinessName) {
                self.accountBusinessName(accountBusinessName);
            }
            if (businessName) {
                self.businessName(businessName);
            }
            if (businessDescription) {
                self.businessDescription(businessDescription);
            }
            if (agentTone !== null) {
                self.agentTone(agentTone.toString());
            }
            if (industry) {
                self.industry(industry);
            }
            if (numberOfEmployees) {
                self.numberOfEmployees(numberOfEmployees);
            }
            if (numberIntentSelection) {
                self.numberIntentSelection(parseInt(numberIntentSelection));
            }
        };

        const _seedValidation = () => {
            self.businessName.validate();
            self.businessDescription.validate();
            self.agentTone.validate();
            self.industry.validate();
            self.numberOfEmployees.validate();
        };

        self.detached = () => {
            _onboardingNavigationManager.detached();
            _sectionValidationDisplay.detached();
            self.mayForwardFromNumbers().forEach(_disposeNumberSubscriptions);
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/welcome/facades/onboardingIdentityFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/welcome/validators/onboardingIdentityValidator');
            _validator = new ValidatorConstructor();

            const OnboardingNavigationManagerConstructor = require('presentation/welcome/common/onboardingNavigationManager');
            _onboardingNavigationManager = new OnboardingNavigationManagerConstructor();
            _onboardingNavigationManager.init();

            return _initialize();
        };

        const _initialize = () => {
            _validator.registerViewModel(self, _facade);

            _promiseFactory.deferIndefinitely(deferredObject => {
                _populateIndustryDropdown();
                _populateNumberOfEmployeesDropdown();

                _facade.getLiveReceptionistOnboarding()
                    .fail(deferredObject.reject)
                    .done((onboardingSession) => {
                        _populateIdentityForm(onboardingSession);
                        _populateForwardFromNumbers(onboardingSession);
                        _persistDefaultNumberIntentSelection(onboardingSession);
                        _persistWizardStarted(onboardingSession);
                        _disposables.push(
                            self.accountBusinessName.subscribe(_handleAccountBusinessNameChanged),
                            self.businessName.subscribe(_handleBusinessNameChanged),
                            self.businessDescription.subscribe(_handleBusinessDescriptionChanged),
                            self.agentTone.subscribe(_handleAgentToneChanged),
                            self.industry.subscribe(_handleIndustryChanged),
                            self.numberOfEmployees.subscribe(_handleNumberOfEmployeesChanged),
                            self.numberIntentSelection.subscribe(_handleNumberIntentSelectionChanged),
                            self.mayForwardFromNumbers.subscribe(_handleMayForwardFromNumbersChanged)
                        );
                        _seedValidation();
                        deferredObject.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

